import React, { forwardRef, useState } from 'react';
import { useForwardedRef, usePropState } from '@anton.bobrov/react-hooks';
import cx from 'classnames';
import { CancelIcon } from '../../../icons/CancelIcon.js';
import { Submit } from './Submit/index.js';
import grayBg from './image/bg.webp.js';
import styles from './index.module.scss.js';

const SearchInput = forwardRef(
  ({
    className,
    style,
    onSubmit,
    onKeyDown,
    onFocus,
    onBlur,
    onChange,
    value: valueProp,
    clearButton,
    buttonType,
    onClick,
    placeholder = "Search",
    size = "default",
    inputTheme = "blue",
    isFocusStyle = false,
    iconSumbitSize,
    ...props
  }, forwardedRef) => {
    const ref = useForwardedRef(forwardedRef);
    const [isCollapsed, setIsCollapsed] = usePropState(size === "collapsable");
    const [value, setValue] = usePropState(valueProp || "");
    const [isInputFocused, setIsInputFocused] = useState(false);
    const setCollapsed = (isBool) => {
      if (size !== "collapsable") {
        return;
      }
      setIsCollapsed(isBool);
    };
    const handleSubmit = () => {
      if (value.length === 0) {
        return;
      }
      onSubmit(value);
    };
    const handleEnter = (event) => {
      if (event.key === "Enter") {
        if (buttonType) {
          onClick?.();
          return;
        }
        handleSubmit();
      } else {
        onKeyDown?.(event);
      }
    };
    const handleFocus = (event) => {
      setIsInputFocused(true);
      setCollapsed(false);
      onFocus?.(event);
    };
    const handleBlur = (event) => {
      setIsInputFocused(false);
      if (value.length === 0) {
        setCollapsed(true);
      }
      onFocus?.(event);
    };
    const handleChange = (event) => {
      if (buttonType) return;
      setValue(event.target.value);
      onChange?.(event);
    };
    return /* @__PURE__ */ React.createElement(
      "div",
      {
        className: cx(styles.box, className, {
          [styles[size]]: !!size,
          [styles.collapsed]: isCollapsed,
          [styles.searchable]: value.length >= 1,
          [styles[inputTheme]]: inputTheme,
          [styles.focused]: isFocusStyle && isInputFocused,
          [styles[iconSumbitSize || ""]]: !!iconSumbitSize,
          "in-focus": isInputFocused
        }),
        style,
        onClick,
        tabIndex: 0,
        role: "button",
        ...props
      },
      inputTheme === "gray" && /* @__PURE__ */ React.createElement(
        "img",
        {
          className: styles.background,
          src: grayBg,
          alt: "search gray background"
        }
      ),
      /* @__PURE__ */ React.createElement(
        "input",
        {
          ...props,
          className: cx(styles.input, {
            [styles["with-clear-button"]]: clearButton
          }),
          ref,
          type: "text",
          onKeyDown: handleEnter,
          onFocus: handleFocus,
          onBlur: handleBlur,
          value,
          onChange: handleChange,
          placeholder
        }
      ),
      clearButton && value && /* @__PURE__ */ React.createElement(
        "button",
        {
          type: "button",
          className: cx(
            styles["clear-button"],
            "search-input__clear-btn",
            {}
          ),
          "aria-label": "clear button",
          onClick: () => {
            if (ref && ref.current) {
              ref.current.value = "";
              const payload = {
                target: {
                  value: ""
                }
              };
              onChange?.(payload);
              setValue("");
            }
          }
        },
        /* @__PURE__ */ React.createElement(CancelIcon, null)
      ),
      /* @__PURE__ */ React.createElement(
        Submit,
        {
          className: cx(styles.submit, "search-input__submit-btn"),
          size,
          onClick: handleSubmit,
          isSearchable: value.length >= 1,
          iconSumbitSize
        }
      )
    );
  }
);
SearchInput.displayName = "Search.SearchInput";

export { SearchInput };
//# sourceMappingURL=index.js.map
